import { useMemo } from 'react';

import { useFetchParametros } from '@/shared/actions/erp/administration/system-parameters';

/**
 * Slug del parámetro del sistema que define los Huawei-Domain-Name que el ERP
 * ofrece al reactivar un cliente.
 *
 * Se administra en Parametrización → Parámetros del sistema:
 *   slug / código : DOMINIOS_HUAWEI_RADIUS
 *   tipo          : ARRAY
 *   valor         : ["default1","psn1","pyme"]
 */
export const SLUG_DOMINIOS_HUAWEI = 'DOMINIOS_HUAWEI_RADIUS';

/**
 * Dominios que se usan hoy en producción. Es el valor de respaldo cuando el
 * parámetro todavía no existe, está desactivado o su contenido no se puede
 * interpretar.
 *
 * Son los mismos dos que estaban hardcodeados antes, así que si el parámetro
 * falta la pantalla se comporta exactamente como siempre: nada se rompe.
 */
const DOMINIOS_POR_DEFECTO = ['default1', 'psn1'];

/**
 * `suspendido` existe en radreply (lo escribe la suspensión por falta de pago)
 * pero NO puede ofrecerse aquí: el backend rechaza reactivar con ese valor
 * (`contrato_view.py`, "No se puede reactivar con el valor ingresado"). Si
 * alguien lo agrega al parámetro por error, se filtra en vez de dejar una
 * opción muerta en el selector.
 */
const DOMINIOS_NO_REACTIVABLES = ['suspendido'];

/** Acepta un array JSON (["a","b"]) o una lista separada por comas (a, b). */
const parsearDominios = (valor?: string | null): string[] | null => {
  if (!valor) return null;
  const texto = valor.trim();
  if (!texto) return null;

  if (texto.startsWith('[')) {
    try {
      const arr = JSON.parse(texto);
      if (!Array.isArray(arr)) return null;
      const limpio = arr.map((v) => String(v).trim()).filter(Boolean);
      return limpio.length > 0 ? limpio : null;
    } catch {
      // Valor mal formado: se cae al respaldo en vez de romper la pantalla.
      return null;
    }
  }

  // Tolerancia: si alguien lo escribe sin corchetes, igual funciona.
  const porComas = texto
    .split(',')
    .map((v) => v.trim().replace(/^["']|["']$/g, ''))
    .filter(Boolean);
  return porComas.length > 0 ? porComas : null;
};

/**
 * Lista de Huawei-Domain-Name que el usuario puede elegir, leída del parámetro
 * del sistema.
 *
 * Importante: este parámetro solo define **qué opciones se ofrecen**. No toca
 * ningún registro existente de radreply: los clientes que hoy tienen `pyme`,
 * `suspendido` o cualquier otro valor lo conservan intacto. Agregar o quitar
 * opciones aquí no migra ni modifica a nadie.
 *
 * Antes de agregar un dominio nuevo tiene que existir configurado en los BRAS
 * (config `aaa domain` de Huawei). Si se envía uno que el equipo no conoce, el
 * cliente falla al autenticar en la reconexión que dispara el Disconnect.
 */
export const useDominiosHuawei = () => {
  const { data, isLoading } = useFetchParametros({
    params: { slug: SLUG_DOMINIOS_HUAWEI, page_size: 1 },
  });

  /**
   * La respuesta viene con TRES niveles: `erpApi.get` devuelve
   * `ApiResponse<T>` ({code, status, data}) y dentro va el paginado
   * ({count, numero_paginas, data: Parametro[]}). O sea `data.data.data[0]`,
   * igual que lo lee ParametrosPage (`?.data?.data`). Saltarse un nivel
   * devuelve undefined en silencio y el selector se queda con el respaldo.
   */
  const parametro = useMemo(
    () => (data as any)?.data?.data?.[0],
    [data],
  );

  const dominios = useMemo(() => {
    // Un parámetro desactivado se ignora: es la forma de volver al respaldo
    // sin tener que borrarlo.
    const activo = parametro?.estado !== false;
    const parseado = activo ? parsearDominios(parametro?.valor) : null;
    const lista = parseado ?? DOMINIOS_POR_DEFECTO;

    // Se quitan los no reactivables y los duplicados, conservando el orden.
    return Array.from(
      new Set(lista.filter((d) => !DOMINIOS_NO_REACTIVABLES.includes(d.toLowerCase()))),
    );
  }, [parametro]);

  return {
    dominios,
    cargando: isLoading,
    /** true cuando se está usando el respaldo porque el parámetro no sirvió. */
    usandoRespaldo: !parametro?.valor,
  };
};
